import type { ClipboardEvent, CSSProperties, Ref } from 'react'
import type { Category } from '../../lib/supabase'
import type { KnownName } from '../../lib/catalog'
import { LIMITS, type DraftIngredient } from '../../lib/recipeDraft'
import { useNameSuggestions } from '../../hooks/useNameSuggestions'
import { CategoryPills } from '../CategoryPills'
import { CloseIcon } from '../icons'

// One ingredient in the recipe editor, built like a list item: its category's
// dot (tap to pick another), the name — matched as you type against the names
// the household uses, quick add's way — and the amount. A name the household
// surely means under another spelling is offered, never put in unasked.

export function IngredientRow({
  g, index, categories, known, tintFor, active, picking, nameRef,
  onName, onChoose, onAmount, onPick, onTogglePicking, onTogglePantry, onRemove, onNext, onPaste, onFocus,
}: {
  g: DraftIngredient
  index: number
  categories: Category[]
  known: KnownName[]
  tintFor: (categoryId: string | null) => string
  /** The row being typed in: only it lists suggestions. */
  active: boolean
  /** Its category pills are open. */
  picking: boolean
  nameRef?: Ref<HTMLInputElement>
  onName: (name: string) => void
  /** A suggested name was taken: like typing it, and the suggestions close. */
  onChoose: (name: string) => void
  onAmount: (amount: string) => void
  onPick: (categoryId: string) => void
  onTogglePicking: () => void
  onTogglePantry: () => void
  onRemove: () => void
  /** Enter in the amount: a new row after this one. */
  onNext: () => void
  onPaste: (e: ClipboardEvent<HTMLInputElement>) => void
  onFocus: () => void
}) {
  const { suggestions, sure } = useNameSuggestions(g.name, known)
  const shown = active ? suggestions.filter(k => k.name !== sure) : []
  const label = g.name.trim() || `ingredient ${index + 1}`
  const category = categories.find(c => c.id === g.categoryId)
  const amountId = `ml-amount-${g.key}`

  return (
    <li className="ml-editor__ingredient" style={{ '--ml-tint': tintFor(g.categoryId) } as CSSProperties}>
      <div className="ml-editor__row ml-editor__row--ingredient">
        <button type="button" className={`ml-editor__cat${category ? '' : ' is-none'}`} aria-expanded={picking}
          aria-label={`Category of ${label}: ${category?.name ?? 'none'}`} onClick={onTogglePicking}>
          <span className="ml-dot" aria-hidden="true" />
        </button>
        <input ref={nameRef} className="ml-input ml-editor__line" value={g.name} autoComplete="off"
          maxLength={LIMITS.ingredientLine} aria-label={`Ingredient ${index + 1}`} placeholder="Potatoes"
          onChange={e => onName(e.target.value)} onFocus={onFocus} onPaste={onPaste}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById(amountId)?.focus() } }} />
        <input id={amountId} className="ml-input ml-editor__amount" value={g.amount} autoComplete="off"
          maxLength={40} aria-label={`Amount ${index + 1}`} placeholder="500 g"
          onChange={e => onAmount(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onNext() } }} />
        <button type="button" className={`ml-editor__pantry${g.pantry ? ' is-on' : ''}`} aria-pressed={g.pantry}
          aria-label={`Pantry: ${label}`} onClick={onTogglePantry}>
          Pantry
        </button>
        <button type="button" className="ml-editor__icon" aria-label={`Remove ${label}`} onClick={onRemove}>
          <CloseIcon size={18} />
        </button>
      </div>

      {sure && (
        <p className="ml-editor__known">
          <span className="ml-caption">Your list calls it “{sure}”</span>
          <button type="button" className="ml-text-btn" aria-label={`Use ${sure}`} onClick={() => onChoose(sure)}>Use</button>
        </p>
      )}

      {shown.length > 0 && (
        <ul className="ml-namefield__list ml-editor__suggest" aria-label={`Names you use for ${g.name.trim()}`}>
          {shown.map(k => (
            <li key={`${k.where}-${k.name}`}>
              <button type="button" className="ml-namefield__option" aria-label={`${k.name}, ${k.where}`} onClick={() => onChoose(k.name)}>
                <span>{k.name}</span>
                <span className="ml-caption">{k.where}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <CategoryPills categories={categories} value={g.categoryId} tintFor={tintFor} onPick={onPick}
          label={`Category of ${label}`} />
      )}
    </li>
  )
}
